import { ApplicationController } from "../application_controller";
import BSN from "bootstrap.native";
import { getMetaValue } from "helpers";

// Currency Helper Class - Centralized currency logic
class CurrencyHelper {
  constructor(controller) {
    this.controller = controller;
  }

  get mainCurrency() {
    const rowElement = this.controller.element.querySelector('.row[data-main-currency]');
    return rowElement?.dataset.mainCurrency;
  }

  get additionalCurrency() {
    const rowElement = this.controller.element.querySelector('.row[data-additional-currency]');
    return rowElement?.dataset.additionalCurrency;
  }

  getCurrencySymbol(currency) {
    const symbols = this.getCurrencySymbols();
    return symbols[currency] || currency;
  }

  getCurrencySymbols() {
    try {
      const rowElement = this.controller.element.querySelector('.row[data-currency-symbols]');
      const currencySymbolsData = rowElement?.dataset.currencySymbols;
      return currencySymbolsData ? JSON.parse(currencySymbolsData) : {};
    } catch (error) {
      console.error('Error parsing currency symbols data:', error);
      return {};
    }
  }

  getExchangeRates() {
    try {
      const rowElement = this.controller.element.querySelector('.row[data-exchange-rates]');
      const exchangeRatesData = rowElement?.dataset.exchangeRates;
      return exchangeRatesData ? JSON.parse(exchangeRatesData) : {
        from_main: {}, to_main: {}, from_additional: {}, to_additional: {}
      };
    } catch (error) {
      console.error('Error parsing exchange rates data:', error);
      return { from_main: {}, to_main: {}, from_additional: {}, to_additional: {} };
    }
  }

  getOtherCurrency(sourceCurrency, targetCurrency) {
    if (sourceCurrency !== this.mainCurrency && sourceCurrency !== this.additionalCurrency) {
      return sourceCurrency;
    } else if (targetCurrency !== this.mainCurrency && targetCurrency !== this.additionalCurrency) {
      return targetCurrency;
    }
    return null;
  }

  isMainToAdditionalConversion(sourceCurrency, targetCurrency) {
    return (sourceCurrency === this.mainCurrency && targetCurrency === this.additionalCurrency) ||
           (sourceCurrency === this.additionalCurrency && targetCurrency === this.mainCurrency);
  }

  getConversionType(sourceCurrency, targetCurrency) {
    const main = this.mainCurrency;
    const add = this.additionalCurrency;
    
    if (sourceCurrency === main && targetCurrency === add) return 'main-to-additional';
    if (sourceCurrency === add && targetCurrency === main) return 'additional-to-main';
    if (sourceCurrency === main || targetCurrency === main) return 'main-involved';
    if (sourceCurrency === add || targetCurrency === add) return 'additional-involved';
    return 'other-to-other';
  }
}

// Rate Loader Class - Unified rate loading logic
class RateLoader {
  constructor(controller, currencyHelper) {
    this.controller = controller;
    this.currencyHelper = currencyHelper;
  }

  showRateFields() {
    const sourceCurrency = this.controller.currentSourceCurrency;
    const targetCurrency = this.controller.currentTargetCurrency;

    if (!sourceCurrency || !targetCurrency) return;

    const conversionType = this.controller.currencyHelper.getConversionType(sourceCurrency, targetCurrency);
    
    this.controller.hideAllRateFields();

    if (conversionType === 'main-to-additional' || conversionType === 'additional-to-main') {
      // Show one field for main <-> additional conversion
      // Always show as "1 [main] = X [additional]"
      this.controller.showRateFieldFixed(this.controller.currencyHelper.mainCurrency, this.controller.currencyHelper.additionalCurrency, 'toMain');
    } else {
      // Show two fields for conversions involving other currencies
      const main = this.controller.currencyHelper.mainCurrency;
      const additional = this.controller.currencyHelper.additionalCurrency;
      const other = this.controller.currencyHelper.getOtherCurrency(sourceCurrency, targetCurrency);
      
      if (other) {
        // First field: "1 [main] = X [other]"
        this.controller.showRateFieldFixed(main, other, 'toMain');
        // Second field: "1 [other] = Y [additional]"
        this.controller.showRateFieldFixed(other, additional, 'fromAdditional');
      }
    }
    
    this.controller.calculateConvertedAmount();
  }

  async fetchRatesFromBackend(sourceCurrency, targetCurrency, amount) {
    const params = new URLSearchParams({
      source_currency: sourceCurrency,
      target_currency: targetCurrency,
      amount: amount
    });
    
    const response = await fetch(`/cashbox/transactions/conversion_rates?${params}`, {
      headers: {
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      }
    });

    if (!response.ok) {
      throw new Error(`Server returned error: ${response.status}`);
    }

    return await response.json();
  }
}

export default class extends ApplicationController {
  static targets = [
    "form",
    "sourceAmount",
    "sourceCurrency",
    "targetCurrency",
    "sourceCurrencyText",
    "targetCurrencyText",
    "balanceDisplay",
    "convertedAmountDisplay",
    "validationMessage",
    "toMainRateField",
    "toMainRateLabel",
    "toMainRateInput",
    "toMainRateUnit",
    "fromAdditionalRateField",
    "fromAdditionalRateLabel",
    "fromAdditionalRateInput",
    "fromAdditionalRateUnit"
  ]

  connect() {
    this.currentSourceCurrency = null;
    this.currentTargetCurrency = null;
    this.availableBalances = {};
    
    if (this.hasFormTarget) {
      const form_id = this.formTarget.id;
      if (form_id === "conversion_update_form") 
        return; // Skip initialization for update form

      // Initialize helper classes
      this.currencyHelper = new CurrencyHelper(this);
      this.rateLoader = new RateLoader(this, this.currencyHelper);
      
      this.updateSelectedCurrencies();
      this.loadAvailableBalances();
      this.updateSourceCurrencyOptions();
      this.updateTargetCurrencyOptions();
      // MANUAL RATES: Changed from loadRates() to showRateFields()
      // TODO: DEPRECATED - Remove in next iteration
      this.rateLoader.showRateFields();
    }
  }

  // === Core Event Handlers ===

  readNumericValue(inputTarget, fallback = 0) {
    if (!inputTarget) return fallback;
    if (inputTarget.numeric) {
      return inputTarget.numeric.getNumber() || fallback;
    }
    return parseFloat(inputTarget.value) || fallback;
  }

  selectSourceCurrency(event) {
    this.currentSourceCurrency = event.currentTarget.value;

    if (!this.isValidCurrencyPair(this.currentSourceCurrency, this.currentTargetCurrency)) {
      const allCurrencies = this.getAllCurrencies();
      const alternativeTarget = allCurrencies.find(c => this.isValidCurrencyPair(this.currentSourceCurrency, c));
      if (alternativeTarget) {
        this.currentTargetCurrency = alternativeTarget;
        this.targetCurrencyTargets.find(btn => btn.value === alternativeTarget).checked = true;
      }
    }

    this.updateUI();
  }

  selectTargetCurrency(event) {
    this.currentTargetCurrency = event.currentTarget.value;

    if (this.currentTargetCurrency === this.currentSourceCurrency) {
      const allCurrencies = this.getAllCurrencies();
      const alternative = allCurrencies.find(c => this.isValidCurrencyPair(c, this.currentTargetCurrency));
      if (alternative) {
        this.currentSourceCurrency = alternative;
        this.sourceCurrencyTargets.find(btn => btn.value === alternative).checked = true;
      }
    }

    this.updateUI();
  }

  updateUI() {
    if (this.hasSourceCurrencyTextTarget) {
      this.sourceCurrencyTextTarget.textContent = this.currentSourceCurrency;
    }
    if (this.hasTargetCurrencyTextTarget) {
      this.targetCurrencyTextTarget.textContent = this.currentTargetCurrency;
    }

    this.updateBalanceDisplay();
    this.updateSourceCurrencyOptions();
    this.updateTargetCurrencyOptions();
    // MANUAL RATES: Changed from loadRates() to showRateFields()
    // TODO: DEPRECATED - Remove in next iteration
    this.rateLoader.showRateFields();
    this.calculateConvertedAmount();
    this.hideValidationError();
    this.validateAmount();
  }

  onAmountChange(event) {
    this.validateAmount();
    // MANUAL RATES: Disabled automatic rate reloading on amount change
    // TODO: DEPRECATED - Remove in next iteration
    this.calculateConvertedAmount();
  }

  onDirectRateChange(event) {
    this.calculateConvertedAmount();
  }

  onExchangeRateFieldChange(event) {
    this.calculateConvertedAmount();
  }

  // === Rate Field Management ===

  updateRateFields(data) {
    this.hideAllRateFields();

    if (data.display_mode === 'one_field') {
      this.showToMainRateField(data.left_field);
    } else if (data.display_mode === 'two_fields') {
      this.showToMainRateField(data.left_field);
      this.showFromAdditionalRateField(data.right_field);
    }
  }

  hideAllRateFields() {
    if (this.hasToMainRateFieldTarget) this.toMainRateFieldTarget.classList.add('hidden');
    if (this.hasFromAdditionalRateFieldTarget) this.fromAdditionalRateFieldTarget.classList.add('hidden');
  }

  showToMainRateField(field_data) {
    if (this.hasToMainRateFieldTarget) {
      this.toMainRateFieldTarget.classList.remove('hidden');
      this.updateRateField('toMain', field_data);
    }
  }

  showFromAdditionalRateField(field_data) {
    if (this.hasFromAdditionalRateFieldTarget) {
      this.fromAdditionalRateFieldTarget.classList.remove('hidden');
      this.updateRateField('fromAdditional', field_data);
    }
  }

  // Show rate field with fixed currency positions 
  showRateFieldFixed(fromCurrency, toCurrency, fieldType) {
    const labelTarget = fieldType === 'toMain' ? this.toMainRateLabelTarget : this.fromAdditionalRateLabelTarget;
    const unitTarget = fieldType === 'toMain' ? this.toMainRateUnitTarget : this.fromAdditionalRateUnitTarget;
    const fieldTarget = fieldType === 'toMain' ? this.toMainRateFieldTarget : this.fromAdditionalRateFieldTarget;

    if (!labelTarget || !unitTarget || !fieldTarget) return;

    fieldTarget.classList.remove('hidden');

    const fromSymbol = this.currencyHelper.getCurrencySymbol(fromCurrency);

    labelTarget.textContent = `1 ${fromSymbol} =`;
    unitTarget.textContent = toCurrency;
  }

  updateRateField(fieldType, data) {
    const labelTarget = fieldType === 'toMain' ? this.toMainRateLabelTarget : this.fromAdditionalRateLabelTarget;
    const unitTarget = fieldType === 'toMain' ? this.toMainRateUnitTarget : this.fromAdditionalRateUnitTarget;
    const inputTarget = fieldType === 'toMain' ? this.toMainRateInputTarget : this.fromAdditionalRateInputTarget;

    if (!labelTarget || !unitTarget || !inputTarget) return;

    const { rate, from_currency, to_currency } = data;
    const fromSymbol = this.currencyHelper.getCurrencySymbol(from_currency);

    labelTarget.textContent = `1 ${fromSymbol} =`;
    unitTarget.textContent = to_currency;

    // MANUAL RATES: Comment out to disable automatic rate population
    // TODO: DEPRECATED - Remove in next iteration
    // if (inputTarget.numeric) {
    //   inputTarget.numeric.set(rate);
    // } else {
    //   inputTarget.value = rate;
    // }
  }

  // === Validation ===

  validateAmount() {
    const amount = this.readNumericValue(this.sourceAmountTarget);
    const amountCents = Math.round(amount * 100);
    const currencyBalance = this.availableBalances.find(b => b.currency === this.currentSourceCurrency);

    if (amountCents > parseFloat(currencyBalance.balance_cents)) {
      this.showValidationError();
      return false;
    } else {
      this.hideValidationError();
      return true;
    }
  }

  showValidationError() {
    if (this.hasValidationMessageTarget) {
      this.validationMessageTarget.style.display = 'block';
      this.validationMessageTarget.innerHTML = `
        <div class="alert alert-danger mb-0 py-2">
          <small>${this.validationMessageTarget.dataset.insufficientFunds}</small>
        </div>
      `;
    }
  }

  hideValidationError() {
    if (this.hasValidationMessageTarget) {
      this.validationMessageTarget.style.display = 'none';
    }
  }

  // === Balance Management ===

  async loadAvailableBalances() {
    try {
      const response = await fetch('/cashbox/transactions/available_balances', {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
        }
      });
      
      if (response.ok) {
        const balances = await response.json();
        this.availableBalances = balances;
        this.updateBalanceDisplay();
      } else {
        console.error('Error loading balances:', response.status);
      }
    } catch (error) {
      console.error('Error loading balances:', error);
    }
  }

  updateBalanceDisplay() {
    if (!this.hasBalanceDisplayTarget || !this.currentSourceCurrency) return;
    
    const currencyBalance = this.availableBalances.find(b => b.currency === this.currentSourceCurrency);
    if (currencyBalance) {
      const balanceClass = currencyBalance.balance_cents > 0 ? 'text-success' : 'text-danger';
      const balanceHTML = `
        <span class="${balanceClass}">${currencyBalance.balance_formatted}</span>
      `;
      this.balanceDisplayTarget.innerHTML = balanceHTML;
    } else {
      this.balanceDisplayTarget.innerHTML = `
        <span class="text-muted">${this.currentSourceCurrency}: 0</span>
      `;
    }
  }

  // === Currency Options Management ===

  updateSourceCurrencyOptions() {
    const sourceCurrencyButtons = document.querySelectorAll('[data-target="cashbox--conversion.sourceCurrency"]');
    sourceCurrencyButtons.forEach(button => {
      button.disabled = false;
      button.closest('.nav-item__radio').classList.remove('disabled');
    });
  }

  updateTargetCurrencyOptions() {
    const targetCurrencyButtons = document.querySelectorAll('[data-target="cashbox--conversion.targetCurrency"]');
    targetCurrencyButtons.forEach(button => {
      const currency = button.value;
      if (currency === this.currentSourceCurrency || !this.isValidCurrencyPair(this.currentSourceCurrency, currency)) {
        button.disabled = true;
        button.closest('.nav-item__radio').classList.add('disabled');
      } else {
        button.disabled = false;
        button.closest('.nav-item__radio').classList.remove('disabled');
      }
    });
  }

  isValidCurrencyPair(sourceCurrency, targetCurrency) {
    if (!sourceCurrency || !targetCurrency) return true;

    if (sourceCurrency === targetCurrency) return false;

    const mainCurrency = this.getMainCurrency();
    const additionalCurrency = this.getAdditionalCurrency();

    const isSourceMainOrAdditional = (sourceCurrency === mainCurrency || sourceCurrency === additionalCurrency);
    const isTargetMainOrAdditional = (targetCurrency === mainCurrency || targetCurrency === additionalCurrency);

    // Allow conversion between main and additional currencies
    if (isSourceMainOrAdditional && isTargetMainOrAdditional) {
      return true;
    }

    // Allow conversion from/to main or additional currency
    if (isSourceMainOrAdditional || isTargetMainOrAdditional) {
      return true;
    }

    // Block direct conversion between other currencies (should go through main/additional)
    return false;
  }

  updateSelectedCurrencies() {
    const selectedSourceCurrency = document.querySelector('input[data-target="cashbox--conversion.sourceCurrency"]:checked');
    const selectedTargetCurrency = document.querySelector('input[data-target="cashbox--conversion.targetCurrency"]:checked');

    if (selectedSourceCurrency) {
      this.currentSourceCurrency = selectedSourceCurrency.value;
      if (this.hasSourceCurrencyTextTarget) {
        this.sourceCurrencyTextTarget.textContent = this.currentSourceCurrency;
      }
    }
    if (selectedTargetCurrency) {
      this.currentTargetCurrency = selectedTargetCurrency.value;
      if (this.hasTargetCurrencyTextTarget) {
        this.targetCurrencyTextTarget.textContent = this.currentTargetCurrency;
      }
    }
  }

  // === Conversion Calculation ===

  calculateConvertedAmount() {
    if (!this.hasConvertedAmountDisplayTarget || !this.currentSourceCurrency || !this.currentTargetCurrency) return;

    const sourceAmount = this.readNumericValue(this.sourceAmountTarget);
    const type = this.getConversionType();
    let exchangeRate = 0;

    const mainCurrency = this.getMainCurrency();
    const additionalCurrency = this.getAdditionalCurrency();
    const otherCurrency = this.getOtherCurrency(); // This will be null if it's main <-> additional

    switch (type) {
      case 'main-to-additional':
        // Field shows "1 [main] = X [additional]"
        exchangeRate = this.readNumericValue(this.toMainRateInputTarget, 1.0);
        break;
        
      case 'additional-to-main':
        // Field shows "1 [main] = X [additional]", but we need additional->main rate
        const mainToAdditionalRate = this.readNumericValue(this.toMainRateInputTarget, 1.0);
        exchangeRate = mainToAdditionalRate > 0 ? 1 / mainToAdditionalRate : 0;
        break;

      case 'main-involved': // e.g., EUR -> USD, USD -> EUR
      case 'additional-involved': // e.g., EUR -> UZS, UZS -> EUR
        // toMainRateInputTarget shows "1 [main] = X [other]"
        // fromAdditionalRateInputTarget shows "1 [other] = Y [additional]"
        
        const mainToOtherRate = this.readNumericValue(this.toMainRateInputTarget, 1.0);
        const otherToAdditionalRate = this.readNumericValue(this.fromAdditionalRateInputTarget, 1.0);

        if (this.currentSourceCurrency === otherCurrency) {
          if (this.currentTargetCurrency === mainCurrency) { // EUR -> USD
            exchangeRate = mainToOtherRate > 0 ? 1 / mainToOtherRate : 0;
          } else if (this.currentTargetCurrency === additionalCurrency) { // EUR -> UZS
            exchangeRate = otherToAdditionalRate;
          }
        } else if (this.currentTargetCurrency === otherCurrency) {
          if (this.currentSourceCurrency === mainCurrency) { // USD -> EUR
            exchangeRate = mainToOtherRate;
          } else if (this.currentSourceCurrency === additionalCurrency) { // UZS -> EUR
            exchangeRate = otherToAdditionalRate > 0 ? 1 / otherToAdditionalRate : 0;
          }
        } else {
          console.error("Unexpected conversion type in calculateConvertedAmount");
          exchangeRate = 0;
        }
        break;

      default:
        exchangeRate = 0;
        break;
    }

    const convertedAmount = sourceAmount * exchangeRate;

    if (this.convertedAmountDisplayTarget.numeric) {
      this.convertedAmountDisplayTarget.numeric.set(convertedAmount);
    } else {
      this.convertedAmountDisplayTarget.value = convertedAmount;
    }

    // Set the actual conversion rate for backend
    this.setActualConversionRate(exchangeRate);
  }

  // === Rate Management ===

  setActualConversionRate(exchangeRate) {
    const actualRateField = document.querySelector('input[name="cashbox_transaction[actual_conversion_rate]"]');
    if (actualRateField) {
      actualRateField.value = exchangeRate;
    }
  }

  // === Form Submission ===

  beforeSubmit(event) {
    const form = event.target;
    const toMainRateField = form.querySelector('input[name="cashbox_transaction[to_main_rate]"]');
    const fromAdditionalRateField = form.querySelector('input[name="cashbox_transaction[from_additional_rate]"]');

    const leftFieldRate = this.readNumericValue(this.toMainRateInputTarget, 1.0);
    const rightFieldRate = this.readNumericValue(this.fromAdditionalRateInputTarget, 1.0);

    if (toMainRateField) toMainRateField.value = leftFieldRate;
    if (fromAdditionalRateField) fromAdditionalRateField.value = rightFieldRate;
  }

  // === Event Handlers ===

  onPostPutSuccess(event) {
    const [data, status, xhr] = event.detail;

    if (data.success && data.redirect_url) {
      window.location.href = data.redirect_url;
    } else if (data.success && data.message) {
      if (window.vNotify) {
        window.vNotify.success({
          text: data.message,
          title: data.notification_title
        });
      }

      if (window.layout_modal_md) {
        window.layout_modal_md.hide();
      }
    }
  }

  onPostPutError(event) {
    const [data, status, xhr] = event.detail;

    if (data.form_content) {
      if (window.layout_modal_md) {
        window.layout_modal_md.setContent(data.form_content);
      }
    } else if (data.message) {
      if (window.vNotify) {
        window.vNotify.error({
          text: data.message,
          title: data.notification_title
        });
      }
    }
  }

  // === Helper Methods (delegates to currency helper) ===

  getMainCurrency() {
    return this.currencyHelper.mainCurrency;
  }

  getMainCurrencySymbol() {
    return this.currencyHelper.getCurrencySymbol(this.currencyHelper.mainCurrency);
  }

  getAdditionalCurrency() {
    return this.currencyHelper.additionalCurrency;
  }

  getAdditionalCurrencySymbol() {
    return this.currencyHelper.getCurrencySymbol(this.currencyHelper.additionalCurrency);
  }

  getCurrencySymbol(currency) {
    return this.currencyHelper.getCurrencySymbol(currency);
  }

  getExchangeRates() {
    return this.currencyHelper.getExchangeRates();
  }


  getConversionType() {
    return this.currencyHelper.getConversionType(this.currentSourceCurrency, this.currentTargetCurrency);
  }

  getAllCurrencies() {
    try {
      const rowElement = this.element.querySelector('.row[data-all-currencies]');
      const allCurrenciesData = rowElement?.dataset.allCurrencies;
      if (allCurrenciesData) {
        return JSON.parse(allCurrenciesData);
      }
    } catch (error) {
      console.error('Error parsing all currencies data:', error);
    }
    return [];
  }

  getOtherCurrency() {
    return this.currencyHelper.getOtherCurrency(this.currentSourceCurrency, this.currentTargetCurrency);
  }

  isMainOrAdditionalCurrency(currency) {
    return currency === this.getMainCurrency() || currency === this.getAdditionalCurrency();
  }
}